// src/pages/TodayBookingsList.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';
import { shouldShowSeatBookingInTodayList } from '../utils/seatTodayListRules';
axios.defaults.withCredentials = true;

/** 業務日切點：每天 01:00 本機時間 → YYYY-MM-DD */
function getDayKeyBy1AM(d = new Date()) {
  const year = d.getFullYear();
  const mon  = d.getMonth();
  const day  = d.getDate();
  const hour = d.getHours();
  const base = new Date(year, mon, day, 0, 0, 0, 0);
  if (hour < 1) base.setDate(base.getDate() - 1);
  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
const bizDay = () => getDayKeyBy1AM();

const pickHM = (v) => {
  if (!v) return '-';
  const m = String(v).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '-';
};
const key4 = (name) => String(name || '').trim().slice(0, 4).toUpperCase();
const isSeatOnFloor = (name, floor) => {
  const k = key4(name);
  if (floor === '1F') return k.startsWith('1F');
  if (floor === '2F') return k.startsWith('2F');
  return true;
};
const levelColor = (lvl = '') => {
  if (/資深/.test(lvl)) return '#7c3aed';
  if (/年會員|月會員/.test(lvl)) return '#2563eb';
  if (/員工/.test(lvl)) return '#f97316';
  return '#6b7280';
};

/** HH:MM → 分鐘 */
function hmToMin(hm) {
  const m = String(hm || '').match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const H = Math.max(0, Math.min(23, parseInt(m[1], 10)));
  const M = Math.max(0, Math.min(59, parseInt(m[2], 10)));
  return H * 60 + M;
}
/** 現在 → 分鐘（本地） */
function nowMin() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}
/** 現在 - 15 分鐘（不處理跨日） */
function nowMinus15Min() {
  return Math.max(0, nowMin() - 15);
}

/** 輕量哈希：只雜湊 UI 用到的欄位 */
function fastHash(obj) {
  const stable = JSON.stringify(obj, Object.keys(obj || {}).sort());
  let h = 0;
  for (let i = 0; i < stable.length; i++) h = (h ^ stable.charCodeAt(i)) >>> 0;
  return h;
}
function listHash(list) {
  if (!Array.isArray(list)) return 0;
  const proj = list.map(r => ({
    id: r.ID,
    hm: r.START_HM || pickHM(r.START_AT),
    cin: !!r.CHECKED_IN,
    can: r.is_cancelled_today === 1,
    rej: r.is_rejected_today === 1,
    num: Number(r.NUMBER) || 0,
    seat: r.SEAT_NAME || '',
    name: r.CUSTOMER_NAME || '',
    phone: r.PHONE || '',
    lvl: r.DISPLAY_LEVEL || '',
    note: r.NOTE || '',
  }));
  return fastHash({ L: proj.length, proj });
}

export default function TodayBookingsList() {
  const [ymd, setYmd] = useState(bizDay());
  const [view, setView] = useState('ALL');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);   // ← 初始為 true，確保初次載入時顯示 loading
  const [err, setErr] = useState('');
  const [savingIds, setSavingIds] = useState({});  // id:true 代表此列正在寫入

  // 狀態：ETag / Hash / 請求鎖 / 中止控制
  const listHashRef = useRef(0);
  const listLengthRef = useRef(0); // 目前列表長度，供 304 分支判斷是否需強制重載（避免閉包過期）
  const etagRef = useRef('');
  const fetchLockRef = useRef(false);
  const abortRef = useRef(null);
  const initialLoadRef = useRef(true); // 追蹤是否為初次載入
  const mountedRef = useRef(true); // 追蹤組件是否已掛載（類似 BookingBoard）
  listLengthRef.current = list.length;

  // 自動滾動觸發版本（只在初次載入時觸發）
  const [listVer, setListVer] = useState(0);
  const listWrapRef = useRef(null);

  // Socket.IO 連線狀態
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // 自適應輪詢：可見時 60s，背景 180s
  const getPollMs = () => (document.visibilityState === 'visible' ? 60000 : 180000);

  const normalizeAndSort = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((r) => ({ ...r, __hm: r.START_HM || pickHM(r.START_AT) }))
      .sort((a, b) => (a.__hm || '').localeCompare(b.__hm || ''));

  // 簡化邏輯：直接使用 useCallback，類似 BookingBoard
  const fetchList = React.useCallback(async (dateStr, { silent = false } = {}) => {
    // 如果沒有提供 dateStr，使用當前的 ymd
    const targetDate = dateStr || ymd;
    
    // 防止重複請求：如果正在請求同一個日期，跳過
    if (fetchLockRef.current) {
      const currentRequestDate = abortRef.current?.targetDate;
      if (currentRequestDate === targetDate) {
        console.warn('[TodayBookingsList] ⚠️ fetchList 被鎖定，且請求相同日期，跳過:', { 
          targetDate, 
          silent,
          timestamp: new Date().toISOString(),
        });
        return;
      }
      // 如果請求不同日期，取消前一個請求
      console.log('[TodayBookingsList] 取消前一個請求（不同日期）:', {
        previousDate: currentRequestDate,
        newDate: targetDate,
      });
      if (abortRef.current?.controller) {
        try { abortRef.current.controller.abort(); } catch {}
      }
    }
    fetchLockRef.current = true;
    
    console.log('[TodayBookingsList] ===== 開始 fetchList =====', {
      targetDate,
      silent,
      isInitialLoad: initialLoadRef.current,
      mounted: mountedRef.current,
      currentListLength: list.length,
      currentHash: listHashRef.current,
      timestamp: new Date().toISOString(),
    });
    
    // 確保 mountedRef 在請求開始時是 true
    if (!mountedRef.current) {
      console.warn('[TodayBookingsList] ⚠️ 組件未掛載，但嘗試載入資料，這不應該發生');
      fetchLockRef.current = false;
      return;
    }

    // 創建新的 AbortController，並記錄目標日期
    const controller = new AbortController();
    abortRef.current = {
      controller,
      targetDate, // 記錄請求的日期，用於判斷是否為相同請求
    };

    // 一律不帶 ETag，每次都是完整重抓，API 回什麼就顯示什麼
    const isInitialLoad = initialLoadRef.current;
    const cfg = {
      params: { day: targetDate, section: '' },
      signal: abortRef.current.controller.signal,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    };

    try {
      setErr('');
      // 只在初次載入或非 silent 時顯示 loading
      if (isInitialLoad || !silent) {
        setLoading(true);
      }

      // 調試日誌
      console.log('[TodayBookingsList] 開始載入資料:', { targetDate, isInitialLoad, silent });
      console.log('[TodayBookingsList] 請求配置:', {
        url: '/api/bookings/today',
        params: cfg.params,
        hasEtag: !!cfg.headers?.['If-None-Match'],
        etag: cfg.headers?.['If-None-Match'] || 'none',
        hasSignal: !!cfg.signal,
      });

      let res;
      try {
        console.log('[TodayBookingsList] 發送 API 請求...', {
          targetDate,
          signalAborted: abortRef.current.controller.signal.aborted,
        });
        res = await axios.get('/api/bookings/today', cfg);
        console.log('[TodayBookingsList] ✅ API 請求完成，狀態碼:', res.status);
      } catch (requestError) {
        const isCanceled = axios.isCancel?.(requestError) || requestError?.name === 'CanceledError' || requestError?.code === 'ERR_CANCELED';
        console.error('[TodayBookingsList] ❌ API 請求失敗:', {
          error: requestError,
          message: requestError?.message,
          code: requestError?.code,
          response: requestError?.response,
          isCanceled,
          targetDate,
          currentAbortTargetDate: abortRef.current?.targetDate,
          signalAborted: abortRef.current?.controller?.signal?.aborted,
        });
        
        // 如果請求被取消，且是初次載入，嘗試重新請求
        if (isCanceled && isInitialLoad) {
          console.warn('[TodayBookingsList] ⚠️ 初次載入請求被取消，嘗試重新請求...');
          // 清除鎖定，允許重新請求
          fetchLockRef.current = false;
          // 延遲一點再重新請求，避免立即重複
          setTimeout(() => {
            if (mountedRef.current && initialLoadRef.current) {
              console.log('[TodayBookingsList] 重新發送初次載入請求');
              fetchList(targetDate, { silent: false });
            }
          }, 100);
          return; // 不繼續處理，等待重新請求
        }
        
        throw requestError; // 重新拋出，讓外層 catch 處理
      }
      console.log('[TodayBookingsList] API 回應:', {
        status: res.status,
        hasData: !!res.data,
        dataType: typeof res.data,
        listType: Array.isArray(res.data?.list) ? 'array' : typeof res.data?.list,
        listLength: Array.isArray(res.data?.list) ? res.data.list.length : 'N/A',
        timestamp: new Date().toISOString(),
      });
      
      if (res.status !== 304) {
        const { data, headers } = res;
        const arr = Array.isArray(data?.list) ? data.list : [];
        const out = normalizeAndSort(arr.filter(shouldShowSeatBookingInTodayList));

        const nextHash = listHash(out);
        console.log('[TodayBookingsList] 處理資料:', {
          rawCount: arr.length,
          normalizedCount: out.length,
          nextHash,
          currentHash: listHashRef.current,
          hashChanged: nextHash !== listHashRef.current,
          isInitialLoad,
          mounted: mountedRef.current,
          timestamp: new Date().toISOString(),
        });
        
        // 檢查組件是否已掛載（類似 BookingBoard）
        if (!mountedRef.current) {
          console.warn('[TodayBookingsList] ⚠️ 組件已卸載，不更新狀態');
          return;
        }
        
        // 新資料是什麼就貼什麼：每次 API 回傳都直接覆寫列表，不比較 hash
        listHashRef.current = nextHash;
        setList(out);
        console.log('[TodayBookingsList] ✅ 更新列表:', out.length, '筆');
        if (isInitialLoad) {
          setListVer(v => v + 1);
          initialLoadRef.current = false;
          console.log('[TodayBookingsList] ✅ 初次載入完成');
        }

        const et = headers?.etag || '';
        if (et) {
          etagRef.current = et;
          console.log('[TodayBookingsList] 更新 ETag:', et);
        }
      } else {
        // 304 表示資料未變（Not Modified），不會有 response body
        console.log('[TodayBookingsList] 收到 304 (Not Modified):', {
          isInitialLoad,
          hasEtag: !!etagRef.current,
          timestamp: new Date().toISOString(),
        });
        
        // 如果是初次載入，必須重新請求（不使用 ETag）以取得資料
        if (isInitialLoad) {
          console.log('[TodayBookingsList] 初次載入收到 304，重新請求（不使用 ETag）');
          // 清除 ETag，強制重新載入
          etagRef.current = '';
          // 重新請求（不使用 ETag）
          const retryRes = await axios.get('/api/bookings/today', {
            params: { day: targetDate, section: '' },
            signal: abortRef.current.controller.signal, // 使用 abortRef 中的 controller
          });
          
          console.log('[TodayBookingsList] 304 retry 回應:', {
            status: retryRes.status,
            hasData: !!retryRes.data,
            listLength: Array.isArray(retryRes.data?.list) ? retryRes.data.list.length : 'N/A',
            timestamp: new Date().toISOString(),
          });
          
          const retryData = retryRes?.data;
          const retryArr = Array.isArray(retryData?.list) ? retryData.list : [];
          const retryOut = normalizeAndSort(retryArr.filter(shouldShowSeatBookingInTodayList));
          const retryHash = listHash(retryOut);
          
          console.log('[TodayBookingsList] 304 retry 處理:', {
            count: retryOut.length,
            hash: retryHash,
            mounted: mountedRef.current,
          });
          
          // 檢查組件是否已掛載
          if (!mountedRef.current) {
            console.warn('[TodayBookingsList] ⚠️ 組件已卸載（304 retry），不更新狀態');
            return;
          }
          
          listHashRef.current = retryHash;
          setList(retryOut);
          console.log('[TodayBookingsList] ✅ 304 retry 更新列表:', retryOut.length, '筆');
          // 初次載入完成
          setListVer(v => v + 1);
          initialLoadRef.current = false;
          
          const retryEt = retryRes?.headers?.etag || '';
          if (retryEt) {
            etagRef.current = retryEt;
            console.log('[TodayBookingsList] 304 retry 更新 ETag:', retryEt);
          }
        } else {
          // 非初次載入的 304：若目前列表為空（用 ref 讀取實際長度，避免閉包過期），強制重新載入
          if (listLengthRef.current === 0) {
            console.warn('[TodayBookingsList] ⚠️ 收到 304 但列表為空，強制重新載入');
            etagRef.current = '';
            fetchList(targetDate, { silent: true });
            return;
          }
          console.log('[TodayBookingsList] ⏭️ 非初次載入的 304，資料未變，跳過');
        }
      }
      } catch (e) {
        const code = e?.code || '';
        if (axios.isCancel?.(e) || e?.name === 'CanceledError' || code === 'ERR_CANCELED') {
          // ignore
        } else {
          // 檢查組件是否已掛載
          if (!mountedRef.current) {
            console.log('[TodayBookingsList] 組件已卸載（錯誤處理），不更新狀態');
            return;
          }
          
          setErr('讀取失敗');
          console.error('[TodayBookingsList] fetch error:', e);
          // 如果是初次載入失敗，確保清空列表並標記為已完成
          if (isInitialLoad) {
            setList([]);
            initialLoadRef.current = false;
          }
        }
    } finally {
      // 確保 loading 狀態正確關閉（只在組件已掛載時）
      if (mountedRef.current && (isInitialLoad || !silent)) {
        setLoading(false);
        console.log('[TodayBookingsList] ✅ 關閉 loading');
      }
      fetchLockRef.current = false;
      console.log('[TodayBookingsList] ===== 結束 fetchList =====', {
        targetDate,
        timestamp: new Date().toISOString(),
      });
    }
  }, [ymd]);

  // === 推送：Socket.IO 即時收後端 bookings:update，有變動就更新列表 ===
  const SOCKET_DEBUG = true;
  useEffect(() => {
    const s = socketIO('/', { path: '/socket.io' });
    socketRef.current = s;

    const onConnect = () => {
      setSocketConnected(true);
      if (SOCKET_DEBUG) console.log('[櫃台-今日預約] 推送 Socket 已連接');
    };
    const onDisconnect = () => {
      setSocketConnected(false);
      if (SOCKET_DEBUG) console.log('[櫃台-今日預約] 推送 Socket 已斷線');
    };

    // 更新：有 row 就合併單列，否則整批 silent refresh
    const onUpdate = ({ row, day: updateDay }) => {
      if (SOCKET_DEBUG) console.log('[櫃台-今日預約] 收到推送 bookings:update', row ? '單筆' : '整批');
      const currentDay = bizDay();
      
      // 如果更新日期與當前顯示日期不同，先更新日期
      if (updateDay && updateDay !== ymd) {
        console.log('[TodayBookingsList] Socket 更新日期不匹配，更新 ymd:', ymd, '->', updateDay);
        setYmd(updateDay);
        // 日期更新會觸發其他 useEffect 重新載入資料，這裡直接返回
        return;
      }
      
      // 只處理當天的更新（與當前顯示的日期一致）
      if (updateDay && updateDay !== currentDay) {
        console.log('[TodayBookingsList] Socket 更新日期不是今天，忽略:', updateDay, 'currentDay:', currentDay);
        return;
      }
      
      if (row) {
        setList((ls) => {
          const exists = ls.some(x => x.ID === row.ID);
          if (exists) {
            const mergedBase = ls.find(x => x.ID === row.ID);
            const merged = { ...mergedBase, ...row, __hm: row.START_HM || pickHM(row.START_AT) };
            if (!shouldShowSeatBookingInTodayList(merged)) {
              const dropped = ls.filter((x) => x.ID !== row.ID);
              listHashRef.current = listHash(dropped);
              return dropped;
            }
            const updated = ls.map((x) => (x.ID === row.ID ? merged : x));
            listHashRef.current = listHash(updated);
            return updated;
          }
          const newItem = { ...row, __hm: row.START_HM || pickHM(row.START_AT) };
          if (!shouldShowSeatBookingInTodayList(newItem)) return ls;
          const updated = [...ls, newItem];
          listHashRef.current = listHash(updated);
          return normalizeAndSort(updated);
        });
        // 移除自動滾動，避免頁面跳動
        // setListVer(v => v + 1);
      } else {
        // 沒有 row，整批刷新（使用 silent，不觸發 loading）
        fetchList(currentDay, { silent: true });
      }
    };
    
    const onUpdateSections = () => {
      // sections 更新時，也刷新列表（使用 silent，不觸發 loading）
      fetchList(bizDay(), { silent: true });
    };

    const onSocketConnect = () => {
      if (SOCKET_DEBUG) console.log('[櫃台-今日預約] 推送 加入 room:', ymd);
      onConnect();
      // 連接後立即加入 room
      s.emit('bookings:join', { day: ymd, section: '' });
    };

    s.on('connect', onSocketConnect);
    s.on('disconnect', onDisconnect);
    s.on('bookings:update', onUpdate);
    s.on('bookings:update-sections', onUpdateSections);

    // 如果已經連接，立即加入 room
    if (s.connected) {
      onSocketConnect();
    }

    return () => {
      try { s.emit('bookings:leave', { day: ymd, section: '' }); } catch {}
      s.off('connect', onSocketConnect);
      s.off('disconnect', onDisconnect);
      s.off('bookings:update', onUpdate);
      s.off('bookings:update-sections', onUpdateSections);
      s.close();
    };
  }, [ymd, fetchList]);

  // 當日期改變時，重新加入 Socket room
  useEffect(() => {
    const s = socketRef.current;
    if (!s || !s.connected) return;
    console.log('[TodayBookingsList] 日期改變，重新加入 room:', ymd);
    s.emit('bookings:join', { day: ymd, section: '' });
  }, [ymd]);

  // 初次載入 + 日期改變時重置 initialLoadRef 並載入資料
  // 簡化邏輯：類似 BookingBoard，直接調用 fetchList
  useEffect(() => {
    console.log('[TodayBookingsList] useEffect 觸發，載入資料:', ymd, 'mounted:', mountedRef.current);
    
    // 標記組件已掛載（類似 BookingBoard）
    mountedRef.current = true;
    
    // 日期改變時重置為初次載入狀態
    initialLoadRef.current = true;
    listHashRef.current = 0; // 重置 hash，確保初次載入時一定會更新
    etagRef.current = ''; // 清除 ETag，確保初次載入時不使用 ETag
    setList([]); // 清空列表，確保顯示 loading
    setErr(''); // 清除錯誤
    setLoading(true); // 立即顯示 loading
    
    // 清除 fetchLock，確保可以發送新請求
    fetchLockRef.current = false;
    
    // 直接調用 fetchList，不使用 silent，確保顯示 loading
    fetchList(ymd, { silent: false });
    
    return () => {
      // 組件卸載時標記為未掛載（類似 BookingBoard）
      // 注意：不要在 cleanup 中 abort 請求，因為這會導致請求被取消
      // 只有在組件真正卸載時才 abort（由其他 cleanup 處理）
      mountedRef.current = false;
      console.log('[TodayBookingsList] useEffect cleanup，組件可能卸載或日期改變');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymd]);

  // 自動換日檢查（業務日 01:00）- 更頻繁檢查，確保跨日時立即刷新
  useEffect(() => {
    let alive = true;
    
    const checkAndUpdateDay = () => {
      if (!alive || !mountedRef.current) return;
      const now = bizDay();
      if (now !== ymd) {
        console.log('[TodayBookingsList] ⚠️ 檢測到日期改變:', ymd, '->', now, '立即刷新所有資料');
        // 日期改變時，先清空列表和重置狀態，然後更新日期（會觸發資料重新載入）
        setList([]);
        listHashRef.current = 0;
        etagRef.current = '';
        initialLoadRef.current = true;
        setYmd(now); // 日期改變會觸發其他 useEffect 重新載入資料
      }
    };
    
    // 更頻繁的跨「業務日」檢查：每 10 秒檢查一次日期是否改變
    // 這樣可以在 1:00 之後的 10 秒內檢測到日期變化
    const dayTick = setInterval(checkAndUpdateDay, 10000); // 每 10 秒檢查一次
    
    // 立即檢查一次
    checkAndUpdateDay();
    
    // 當頁面重新可見時（例如早上打開瀏覽器），立即檢查日期
    const onVisible = () => {
      if (document.visibilityState === 'visible' && mountedRef.current) {
        checkAndUpdateDay();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    
    // 當視窗重新聚焦時，也檢查日期
    const onFocus = () => {
      if (mountedRef.current) {
        checkAndUpdateDay();
      }
    };
    window.addEventListener('focus', onFocus);

    return () => {
      alive = false;
      clearInterval(dayTick);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [ymd]);

  // === 輪詢 + 推送：每分鐘重新讀取完整資料，API 回什麼就顯示什麼 ===
  const POLL_MS = 60 * 1000;
  const DEBUG = true; // 除錯用，可改 false 關閉 log
  useEffect(() => {
    if (!mountedRef.current) return;
    let timer = null;
    let stopped = false;
    if (DEBUG) console.log('[櫃台-今日預約] 輪詢 effect 啟動', { visibility: document.visibilityState, ymd });

    const tick = () => {
      if (stopped || !mountedRef.current) return;
      if (document.visibilityState !== 'visible') {
        if (DEBUG) console.log('[櫃台-今日預約] 輪詢 tick 跳過（頁面不可見）');
        return;
      }
      const now = bizDay();
      if (now !== ymd) {
        setYmd(now);
        return;
      }
      if (DEBUG) console.log('[櫃台-今日預約] 輪詢 tick 執行，定時重抓完整資料', new Date().toLocaleTimeString('zh-TW'));
      fetchList(now, { silent: true }).catch((e) => {
        console.warn('[櫃台-今日預約] 輪詢失敗:', e?.message || e);
      });
    };

    const start = () => {
      if (stopped) return;
      if (DEBUG) console.log('[櫃台-今日預約] 輪詢 start()', { visibility: document.visibilityState });
      tick();
      if (timer) clearInterval(timer);
      timer = setInterval(tick, POLL_MS);
    };

    const stop = () => {
      stopped = true;
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      if (DEBUG) console.log('[櫃台-今日預約] 輪詢 stop()');
    };

    const onVis = () => {
      if (document.hidden) {
        stop();
      } else {
        stopped = false;
        start();
      }
    };

    const onPageShow = () => {
      if (document.visibilityState === 'visible') {
        stopped = false;
        start();
      }
    };

    const onFocus = () => {
      if (document.visibilityState === 'visible') {
        stopped = false;
        start();
      }
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      if (DEBUG) console.log('[櫃台-今日預約] 輪詢 effect cleanup');
    };
  }, [fetchList, ymd]);

  // 切換入場（checked_in）
  const toggleCheckIn = async (row, nextVal) => {
    const id = row?.ID;
    if (!id || savingIds[id]) return;
    if (id === -99999) return; // 志工小幫手不可簽到
    setSavingIds((m) => ({ ...m, [id]: true }));
    try {
      const { data } = await axios.patch(`/api/bookings/${id}`, { checked_in: !!nextVal });
      const newRow = data?.row || null;
      const pairedRow = data?.pairedRow || null;
      if (newRow || pairedRow) {
        setList((old) => {
          let cp = old.slice();
          const apply = (nr) => {
            if (!nr?.ID) return;
            const idx = cp.findIndex((x) => x.ID === nr.ID);
            if (idx >= 0) {
              cp[idx] = { ...cp[idx], ...nr, __hm: nr.START_HM || pickHM(nr.START_AT) };
            } else {
              cp.push({ ...nr, __hm: nr.START_HM || pickHM(nr.START_AT) });
            }
          };
          apply(newRow);
          apply(pairedRow);
          listHashRef.current = listHash(cp);
          return cp;
        });
      }
    } catch (e) {
      console.warn('[toggleCheckIn] fail:', e?.message || e);
      alert('更新入場狀態失敗，請稍後重試');
    } finally {
      setSavingIds((m) => {
        const { [id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const list1F = useMemo(() => list.filter((r) => isSeatOnFloor(r.SEAT_NAME, '1F')), [list]);
  const list2F = useMemo(() => list.filter((r) => isSeatOnFloor(r.SEAT_NAME, '2F')), [list]);
  const TWO_F_SECTIONS = ['2F-H', '2F-L', '2F-P', '2F-Q', '2F-W'];
  const SECTION_NAME_MAP = {
    '夜貓子方案': '夜貓子方案', // 特殊分區
    '1F-C': '1F咖啡廳座位',
    '2F-H': '2F-H 開放區高腳座位',
    '2F-L': '2F-L 開放區升降桌座',
    '2F-P': '2F-P 開放區面牆位',
    '2F-Q': '2F-Q 專注區大桌座位',
    '2F-W': '2F-W 專注區面窗座位',
  };
  const list2Map = useMemo(() => {
    const m = {};
    TWO_F_SECTIONS.forEach((k) => (m[k] = []));
    m['夜貓子方案'] = [];
    list.forEach((r) => {
      const seatName = String(r?.SEAT_NAME || '').trim();
      const k = seatName === '夜貓子方案' ? '夜貓子方案' : key4(seatName);
      if (m[k]) m[k].push(r);
    });
    return m;
  }, [list]);
  const sum = (rows) => rows.reduce((s, r) => s + (Number(r.NUMBER) || 0), 0);

  const filtered = useMemo(() => {
    if (view === 'ALL') return list;
    if (view === '1F') return list1F;
    if (view === '2F') return list2F;
    if (TWO_F_SECTIONS.includes(view)) return list2Map[view] || [];
    if (view === '夜貓子方案') return list2Map['夜貓子方案'] || [];
    return list;
  }, [view, list, list1F, list2F, list2Map]);

  // ★ 只在初次載入完成時自動滾動到「第一筆 時間 ≥ (現在-15分)」。
  // 後續更新不會觸發自動滾動，避免頁面跳動。
  useEffect(() => {
    // 只在初次載入完成時（listVer > 0）才自動滾動
    if (listVer === 0) return;
    if (!filtered?.length) return;
    const wrap = listWrapRef.current;
    if (!wrap) return;

    const threshold = nowMinus15Min();

    // 找到目標 index
    let targetIdx = -1;
    for (let i = 0; i < filtered.length; i++) {
      const hm = filtered[i].__hm || pickHM(filtered[i].START_AT);
      const mm = hmToMin(hm);
      if (mm !== null && mm >= threshold) { targetIdx = i; break; }
    }
    if (targetIdx < 0) targetIdx = filtered.length - 1; // 全都小於 threshold → 最後一筆

    const targetId = filtered[targetIdx]?.ID;
    if (!targetId) return;

    // DOM 找到對應列
    const el = wrap.querySelector(`[data-id="${targetId}"]`);
    if (!el) return;

    // 更穩定的可視檢查：用 bounding rect 相對 wrap
    const wrapRect = wrap.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const visible = elRect.top >= wrapRect.top && elRect.bottom <= wrapRect.bottom;
    if (visible) return;

    // 捲動到該列（保留 8px 上方留白）
    const top = el.offsetTop - 8;
    wrap.scrollTo({ top, behavior: 'auto' });
  }, [listVer, view, filtered]);

  const Tabs = () => {
    const items = [
      { key: 'ALL', label: '總攬', count: sum(list) },
      { key: '1F', label: '1F', count: sum(list1F) },
      { key: '2F', label: '2F', count: sum(list2F) },
      ...TWO_F_SECTIONS.map((k) => ({
        key: k,
        label: SECTION_NAME_MAP[k] || k,
        count: sum(list2Map[k] || []),
      })),
      // 如果有夜貓子方案的預約，顯示該分頁
      ...(list2Map['夜貓子方案'] && list2Map['夜貓子方案'].length > 0 ? [{
        key: '夜貓子方案',
        label: '夜貓子方案',
        count: sum(list2Map['夜貓子方案'])
      }] : []),
    ];
    return (
      <div style={st.tabs}>
        {items.map((it) => {
          const active = it.key === view;
          return (
            <button
              key={it.key}
              onClick={() => setView(it.key)}
              style={{
                ...st.tabBtn,
                background: active 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                color: active ? '#fff' : '#4c51bf',
                borderColor: active ? '#667eea' : 'rgba(99, 102, 241, 0.2)',
                boxShadow: active ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
              title={`${it.label}（總人數：${it.count}）`}
            >
              <span
                style={{
                  fontWeight: 900,
                  maxWidth: 220,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: 16,
                }}
              >
                {it.label}
              </span>
              <span
                style={{
                  ...st.countBadge,
                  background: active 
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: active ? '#fff' : '#fff',
                  boxShadow: active ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.2)',
                }}
              >
                {it.count}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  // 時間檢查：每分鐘更新一次，用於標記過期預約
  const [timeCheckTick, setTimeCheckTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeCheckTick(t => t + 1);
    }, 60000); // 每分鐘更新一次
    return () => clearInterval(timer);
  }, []);

  const Row = ({ r }) => {
    // 使用 timeCheckTick 確保每分鐘重新計算一次狀態（即使資料沒變）
    // eslint-disable-next-line no-unused-vars
    const _tick = timeCheckTick;
    
    const hm = r.__hm || r.START_HM || pickHM(r.START_AT);
    const voidedToday =
      r.is_cancelled_today === 1 ||
      r.is_rejected_today === 1;
    const cancelled = voidedToday;
    const checkedIn = !!r.CHECKED_IN;
    const saving = !!savingIds[r.ID];
    const lvl = r.DISPLAY_LEVEL || '一般顧客';
    const isVirtual = r.ID === -99999; // 志工小幫手不可簽到

    // 檢查是否超過預約時間（START_AT 已過期）
    const isExpired = (() => {
      if (!r.START_AT || checkedIn || cancelled) return false;
      try {
        const startTime = new Date(r.START_AT);
        const now = new Date();
        // 如果預約時間已過，標記為過期
        return startTime < now;
      } catch {
        return false;
      }
    })();
    
    // 逾時（未入場 & 未取消 & 未過期 & 開始時間 <= 現在-15）
    const threshold = nowMinus15Min();
    const hmMin = hmToMin(hm);
    const isLate = !checkedIn && !cancelled && !isExpired && hmMin !== null && hmMin <= threshold;

    // 綜合狀態判斷：過期 > 臨時取消 > 逾時 > 已入場 > 正常
    // 使用 timeCheckTick 確保每分鐘重新計算一次狀態
    const rowBorder = checkedIn
      ? '#16a34a'
      : cancelled
      ? '#ef4444'
      : isExpired
      ? '#dc2626'
      : isLate
      ? '#f472b6'
      : 'rgba(0,0,0,.12)';

    const rowBg = checkedIn
      ? '#dcfce7'
      : cancelled
      ? '#fee2e2'
      : isExpired
      ? '#fee2e2'
      : isLate
      ? '#fce7f3'
      : '#fff';

    return (
      <div
        data-id={r.ID}
        data-hm={hm}
        style={{
          borderRadius: 14,
          border: `2px solid ${rowBorder}`,
          background: rowBg,
          padding: '14px 16px',
          display: 'grid',
          gap: 10,
          boxShadow: checkedIn 
            ? '0 4px 12px rgba(22, 163, 74, 0.15)'
            : cancelled
              ? '0 4px 12px rgba(239, 68, 68, 0.15)'
              : isExpired
                ? '0 4px 12px rgba(220, 38, 38, 0.15)'
                : isLate
                  ? '0 4px 12px rgba(244, 114, 182, 0.15)'
                  : '0 2px 6px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s',
        }}
      >
        {/* 第一排：時間 / 取消徽章 / 入場勾選 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#111', lineHeight: 1 }}>{hm}</div>
            {cancelled && (
              <span
                style={{
                  padding: '4px 10px',
                  fontSize: 14,
                  fontWeight: 900,
                  borderRadius: 999,
                  border: '1.5px solid #ef4444',
                  color: '#b91c1c',
                  background: '#fee2e2',
                }}
              >
                {r.is_rejected_today === 1 ? '今日已拒絕' : '今日臨時取消'}
              </span>
            )}
            {isExpired && !cancelled && !checkedIn && (
              <span
                style={{
                  padding: '4px 10px',
                  fontSize: 14,
                  fontWeight: 900,
                  borderRadius: 999,
                  border: '1.5px solid #dc2626',
                  color: '#991b1b',
                  background: '#fee2e2',
                }}
              >
                已過期
              </span>
            )}
            {isLate && !cancelled && !checkedIn && !isExpired && (
              <span
                style={{
                  padding: '4px 10px',
                  fontSize: 14,
                  fontWeight: 900,
                  borderRadius: 999,
                  border: '1.5px solid #f472b6',
                  color: '#a11e6a',
                  background: '#fce7f3',
                }}
              >
                已超過入場時間
              </span>
            )}
          </div>

          {/* 入場核取 + 文字（勾取後顯示「已入場」） */}
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderRadius: 10,
              border: `1.5px solid ${checkedIn ? '#16a34a' : 'rgba(0,0,0,.18)'}`,
              background: checkedIn ? '#bbf7d0' : '#fff',
              cursor: (saving || isVirtual) ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              opacity: isVirtual ? 0.7 : 1,
            }}
            title={
              isVirtual
                ? '志工小幫手不需簽到'
                : (checkedIn ? '點擊可取消入場' : '勾選表示入場')
            }
          >
            <input
              type="checkbox"
              checked={checkedIn}
              disabled={saving || isVirtual}
              onChange={(e) => !isVirtual && toggleCheckIn(r, e.target.checked)}
              style={{
                width: 28,
                height: 28,
                accentColor: '#16a34a',
                cursor: (saving || isVirtual) ? 'not-allowed' : 'pointer',
              }}
            />
            <span
              style={{
                fontSize: 18,
                fontWeight: 900,
                letterSpacing: '.02em',
                color: checkedIn ? '#166534' : '#111',
              }}
            >
              {checkedIn ? '已入場' : '入場'}
            </span>
          </label>
        </div>

        {/* 第二排：預約編號、姓名、電話、等級 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {String(r.ORDER_ID || r.ORDER_NO || '').trim() ? (
            <span style={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>
              預約編號：{String(r.ORDER_ID || r.ORDER_NO).trim()}
            </span>
          ) : null}
          <b style={{ fontSize: 20 }}>{r.CUSTOMER_NAME || '（未填姓名）'}</b>
          <span style={{ fontSize: 20, letterSpacing: '0.4px' }}>{r.PHONE || ''}</span>
          <span
            style={{
              padding: '2px 10px',
              fontSize: 12,
              fontWeight: 900,
              borderRadius: 999,
              color: '#fff',
              background: levelColor(lvl),
            }}
          >
            {lvl}
          </span>
        </div>

        {/* 第三排：人數與座位 */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: '#111', fontSize: 18 }}>
          <span>
            人數：<b>{r.NUMBER ?? '-'}</b>
          </span>
          <span>
            座位：<b>{r.SEAT_NAME || '-'}</b>
          </span>
          {r.PAIR_LABEL_UI ? (
            <span style={{ color: '#16a34a', fontWeight: 800 }}>{r.PAIR_LABEL_UI}</span>
          ) : null}
          {String(r?.SEAT_NAME || '').trim() === '夜貓子方案' && String(r?.NIGHT_OWL_PLACEMENT || '').trim() ? (
            <span>
              擺放：<b>{String(r.NIGHT_OWL_PLACEMENT).trim()}</b>
            </span>
          ) : null}
        </div>

        {/* 備註 */}
        {r.NOTE && <div style={{ fontSize: 16, color: '#111' }}>備註：{r.NOTE}</div>}
      </div>
    );
  };

  return (
    <section style={st.leftCol}>
      <div style={st.leftHeader}>
        <div style={{ 
          fontWeight: 1000, 
          fontSize: 18,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          📅 今日預約（{ymd}）
        </div>

        {/* 右側小狀態：不換清單，只顯示載入 / 錯誤 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {loading && (
            <span style={{ fontSize: 12, color: '#555' }}>更新中…</span>
          )}
          {err && (
            <span style={{ fontSize: 12, color: '#b91c1c' }}>{err}</span>
          )}
        </div>
      </div>

      <Tabs />

      <div style={st.listWrap} ref={listWrapRef}>
        {/* 固定渲染清單：載入/錯誤都不替換 DOM，避免捲軸歸零 */}
        {(() => {
          // 調試：記錄渲染狀態
          if (process.env.NODE_ENV === 'development') {
            console.log('[TodayBookingsList] 渲染狀態:', {
              loading,
              listLength: list.length,
              filteredLength: filtered.length,
              view,
              err,
              timestamp: new Date().toISOString(),
            });
          }
          
          if (loading && filtered.length === 0) {
            return <div style={{ padding: 8, fontSize: 14, color: '#666' }}>讀取中…</div>;
          }
          if (err) {
            return <div style={{ padding: 8, fontSize: 14, color: '#b91c1c' }}>錯誤：{err}</div>;
          }
          if (filtered.length === 0) {
            return (
              <div style={{ padding: 8, fontSize: 14 }}>
                今天沒有預約（此分頁）
                {process.env.NODE_ENV === 'development' && (
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    調試：總列表 {list.length} 筆，當前分頁 {view}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div style={{ display: 'grid', gap: 10 }}>
              {filtered.map((r) => (
                <Row key={r.ID} r={r} />
              ))}
            </div>
          );
        })()}
      </div>
    </section>
  );
}

const st = {
  leftCol: {
    minWidth: 0,
    width: '100%', // 確保寬度填滿父容器
    height: '100%', // 確保高度填滿父容器
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
    gap: 10,
    border: '2px solid #e0e7ff',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
    padding: 12,
    boxSizing: 'border-box',
    minHeight: 0,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.08)',
    // 確保在 MemberPanelShell 中正確顯示
    position: 'relative',
  },
  leftHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingBottom: 8,
    borderBottom: '2px solid rgba(99, 102, 241, 0.1)',
  },
  tabs: { display: 'flex', gap: 8, overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: 4 },
  tabBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 10,
    border: '2px solid rgba(99, 102, 241, 0.2)',
    fontSize: 15,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  },
  listWrap: {
    gridRow: 3,
    minHeight: 0,
    height: '100%',
    overflowY: 'auto',
    paddingRight: 8,
    background: 'linear-gradient(180deg, #fafbff 0%, #ffffff 100%)',
    border: '2px solid rgba(99, 102, 241, 0.1)',
    borderRadius: 12,
    position: 'relative',
  },
};
